import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  type AccountRole,
  type CustomerAccount,
  CustomerAccountSchema,
  customerAccountKeys,
} from '@/entities/customer-account'
import { apiRequest } from '@/shared/api'

export type CreateAccountVars = {
  full_name: string
  login: string
  password: string
  job_title?: string
  role: AccountRole
}

export type EditAccountVars = {
  full_name: string
  job_title?: string
  role: AccountRole
  password?: string
}

const parse = (body: unknown): CustomerAccount => CustomerAccountSchema.parse(body)

export function useCreateAccount(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: CreateAccountVars) =>
      parse(
        await apiRequest({
          method: 'POST',
          url: '/customer-accounts',
          body: { company_id: companyId, ...vars },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerAccountKeys.byCompany(companyId) })
    },
  })
}

export function useEditAccount(accountId: string, companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: EditAccountVars) =>
      parse(
        await apiRequest({ method: 'PATCH', url: `/customer-accounts/${accountId}`, body: vars }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerAccountKeys.byCompany(companyId) })
    },
  })
}

export function useSetAccountStatus(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; active: boolean }) =>
      parse(
        await apiRequest({
          method: 'POST',
          url: `/customer-accounts/${vars.id}/${vars.active ? 'activate' : 'deactivate'}`,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerAccountKeys.byCompany(companyId) })
    },
  })
}
